import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, InputNumber, Modal, Space, Switch, Table, Tag, Typography, Upload, message } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { createAndroidVersion, getAndroidVersionConfig, getQiniuUploadToken, type AdminAndroidVersionItem } from '../services/api';
import { parseApkManifestVersionInfo } from '../utils/apkManifest';

const AndroidVersionConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [rows, setRows] = useState<AdminAndroidVersionItem[]>([]);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsingApk, setParsingApk] = useState(false);
  const [form] = Form.useForm();

  const sanitizeVersionPart = (value: string) => {
    const normalized = (value || '').trim().replace(/[^0-9a-zA-Z._-]+/g, '_');
    const cleaned = normalized.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    return cleaned || 'unknown';
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAndroidVersionConfig();
      if (res.code !== 0) {
        message.error(res.message || '加载安卓版本列表失败');
        return;
      }
      setRows((res.data?.list || []) as AdminAndroidVersionItem[]);
    } catch (error) {
      message.error('加载安卓版本列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const highestVersion = useMemo(() => {
    if (!rows.length) return null;
    return rows.reduce((max, item) => {
      if (!max) return item;
      return item.version_code > max.version_code ? item : max;
    }, rows[0] as AdminAndroidVersionItem | null);
  }, [rows]);

  const onCreate = async (values: {
    version_name: string;
    version_code: number;
    download_url: string;
    force_update: boolean;
    update_notes?: string;
  }) => {
    setSaving(true);
    try {
      const payload = {
        version_name: (values.version_name || '').trim(),
        version_code: Number(values.version_code || 0),
        download_url: (values.download_url || '').trim(),
        force_update: Boolean(values.force_update),
        update_notes: (values.update_notes || '').trim(),
      };
      const res = await createAndroidVersion(payload);
      if (res.code !== 0) {
        message.error(res.message || '新增版本失败');
        return;
      }
      message.success('新增版本成功');
      setVisible(false);
      setUploadedUrl('');
      setFileList([]);
      setParsingApk(false);
      form.resetFields();
      await loadList();
    } catch (error) {
      message.error('新增版本失败');
    } finally {
      setSaving(false);
    }
  };

  const uploadProps: UploadProps = {
    accept: '.apk,.aab',
    maxCount: 1,
    fileList,
    showUploadList: true,
    beforeUpload: async (rawFile) => {
      const file = rawFile as File;
      if (!file.name.toLowerCase().endsWith('.apk')) {
        message.info('仅 APK 文件支持自动解析版本信息，当前文件请手动填写。');
        return true;
      }

      setParsingApk(true);
      try {
        const parsed = await parseApkManifestVersionInfo(file);
        if (parsed.versionName) {
          form.setFieldValue('version_name', parsed.versionName);
        }
        if (typeof parsed.versionCode === 'number' && Number.isFinite(parsed.versionCode) && parsed.versionCode > 0) {
          form.setFieldValue('version_code', parsed.versionCode);
        }

        if (parsed.versionName || parsed.versionCode) {
          message.success(`已解析安装包版本信息：${parsed.versionName || '-'} / ${parsed.versionCode || '-'}`);
        } else {
          message.warning('未从 APK 中解析到版本信息，请手动填写。');
        }
      } catch (error) {
        message.warning('APK 版本信息解析失败，请手动填写。');
      } finally {
        setParsingApk(false);
      }

      return true;
    },
    onChange: ({ fileList: next }) => {
      setFileList(next.slice(-1));
    },
    customRequest: async (options) => {
      try {
        const file = options.file as File;
        const tokenRes = await getQiniuUploadToken();
        if (tokenRes.code !== 0 || !tokenRes.data?.token) {
          options.onError?.(new Error(tokenRes.message || '获取上传凭证失败'));
          message.error(tokenRes.message || '获取上传凭证失败');
          return;
        }

        const uploadMeta = tokenRes.data;
        const filename = file.name || 'package.apk';
        const extIndex = filename.lastIndexOf('.');
        const ext = extIndex >= 0 ? filename.substring(extIndex).toLowerCase() : '.apk';
        const dateDir = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const rawVersionName = String(form.getFieldValue('version_name') || '').trim();
        const versionCodeValue = Number(form.getFieldValue('version_code') || 0);
        const hasValidVersionCode = Number.isFinite(versionCodeValue) && versionCodeValue > 0;
        if (!rawVersionName || !hasValidVersionCode) {
          options.onError?.(new Error('请先填写版本号和版本码'));
          message.error('请先填写版本号和版本码，再上传安装包');
          return;
        }
        const versionName = sanitizeVersionPart(rawVersionName);
        const versionCode = String(Math.trunc(versionCodeValue));
        const key = `notifyapp/android/releases/${dateDir}/notifyapp-v${versionName}-${versionCode}${ext}`;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('key', key);
        formData.append('token', uploadMeta.token);

        const uploadResp = await axios.post(uploadMeta.upload_url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploadedKey = uploadResp?.data?.key || key;
        const domain = (uploadMeta.domain || '').replace(/\/+$/, '');
        const url = `${domain}/${uploadedKey}`;

        form.setFieldValue('download_url', url);
        setUploadedUrl(url);
        options.onSuccess?.(uploadResp.data);
        message.success('安装包上传成功');
      } catch (error) {
        options.onError?.(error as Error);
        message.error('安装包上传失败');
      }
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          安卓版本管理
        </Typography.Title>
        <Alert
          type="info"
          showIcon
          message="App 登录页检查更新时，会自动使用版本码最高的一条记录。"
        />
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <div>
            当前最高版本：
            {highestVersion ? (
              <Tag color="green" style={{ marginLeft: 8 }}>
                {highestVersion.version_name} (code {highestVersion.version_code})
              </Tag>
            ) : (
              <Tag color="red" style={{ marginLeft: 8 }}>
                暂无版本
              </Tag>
            )}
          </div>
          <Button type="primary" onClick={() => setVisible(true)}>
            新增版本
          </Button>
        </Space>

        <Table<AdminAndroidVersionItem>
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={false}
          columns={[
            { title: '版本号', dataIndex: 'version_name', key: 'version_name' },
            { title: '版本码', dataIndex: 'version_code', key: 'version_code' },
            {
              title: '更新方式',
              dataIndex: 'force_update',
              key: 'force_update',
              render: (force: boolean) => (force ? <Tag color="red">强制更新</Tag> : <Tag color="blue">可选更新</Tag>),
            },
            {
              title: '下载地址',
              dataIndex: 'download_url',
              key: 'download_url',
              render: (url: string) => (
                <Typography.Text copyable={{ text: url }} style={{ maxWidth: 420 }} ellipsis>
                  {url}
                </Typography.Text>
              ),
            },
            {
              title: '更新时间',
              dataIndex: 'created_at',
              key: 'created_at',
              render: (v: number) => (v ? new Date(v * 1000).toLocaleString() : '-'),
            },
          ]}
        />
      </Space>

      <Modal
        title="新增安卓版本"
        open={visible}
        onCancel={() => {
          setVisible(false);
          setUploadedUrl('');
          setFileList([]);
          setParsingApk(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ force_update: false }}
          onFinish={onCreate}
        >
          <Form.Item
            label="版本号"
            name="version_name"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="例如：1.0.3" />
          </Form.Item>

          <Form.Item
            label="版本码"
            name="version_code"
            rules={[{ required: true, message: '请输入版本码' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="例如：3" />
          </Form.Item>

          <Form.Item label="安装包上传">
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>上传到云端</Button>
            </Upload>
            {parsingApk && (
              <Typography.Text type="secondary">正在解析 APK 版本信息...</Typography.Text>
            )}
          </Form.Item>

          <Form.Item name="download_url" hidden rules={[{ required: true, message: '请先上传安装包' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="上传结果">
            {uploadedUrl ? (
              <Typography.Text copyable={{ text: uploadedUrl }} style={{ maxWidth: 460 }} ellipsis>
                {uploadedUrl}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">请先上传安装包，系统将自动生成下载链接</Typography.Text>
            )}
          </Form.Item>

          <Form.Item label="强制更新" name="force_update" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>

          <Form.Item label="更新说明" name="update_notes">
            <Input.TextArea rows={4} placeholder="可选" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving || parsingApk}>
                确认新增
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AndroidVersionConfig;
